import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { 
  Gift, ChevronRight, ChevronLeft, CreditCard, Mail, User, DollarSign, 
  Loader2, Check, Heart, PartyPopper, Snowflake, Baby, Star, Calendar, Sparkles, Cake
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useBranding } from '../contexts/BrandingContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pre-designed Gift Card Templates with visuals
const GIFT_CARD_DESIGNS = [
  {
    id: 'birthday',
    name: 'Birthday',
    icon: Cake,
    backgroundColor: '#FEF3C7',
    accentColor: '#F59E0B',
    textColor: '#92400E',
    pattern: 'confetti',
    tagline: 'Celebrate Another Year of Beauty!'
  },
  {
    id: 'thank-you',
    name: 'Thank You',
    icon: Heart,
    backgroundColor: '#FCE7F3',
    accentColor: '#EC4899',
    textColor: '#9D174D',
    pattern: 'hearts',
    tagline: 'A Token of Appreciation'
  },
  {
    id: 'holiday',
    name: 'Holiday',
    icon: Snowflake,
    backgroundColor: '#DBEAFE',
    accentColor: '#3B82F6',
    textColor: '#1E40AF',
    pattern: 'snowflakes',
    tagline: 'Warm Wishes & Relaxation'
  },
  {
    id: 'new-baby',
    name: 'New Baby',
    icon: Baby,
    backgroundColor: '#D1FAE5',
    accentColor: '#10B981',
    textColor: '#065F46',
    pattern: 'stars',
    tagline: 'Welcome to the World, Little One!'
  },
  {
    id: 'anniversary',
    name: 'Anniversary',
    icon: Star,
    backgroundColor: '#EDE9FE',
    accentColor: '#8B5CF6',
    textColor: '#5B21B6',
    pattern: 'sparkles',
    tagline: 'Celebrating Love & Togetherness'
  },
  {
    id: 'congratulations',
    name: 'Congratulations',
    icon: PartyPopper,
    backgroundColor: '#FEE2E2',
    accentColor: '#EF4444',
    textColor: '#991B1B',
    pattern: 'confetti',
    tagline: 'Cheers to Your Achievement!'
  },
  {
    id: 'just-because',
    name: 'Just Because',
    icon: Sparkles,
    backgroundColor: '#F3E8FF',
    accentColor: '#A855F7',
    textColor: '#6B21A8',
    pattern: 'dots',
    tagline: 'Because You Deserve It'
  },
  {
    id: 'spa-day',
    name: 'Spa Day',
    icon: Gift,
    backgroundColor: '#CCFBF1',
    accentColor: '#14B8A6',
    textColor: '#0F766E',
    pattern: 'waves',
    tagline: 'Relax, Refresh, Rejuvenate'
  }
];

const DENOMINATIONS = [50, 75, 100, 150, 200, 250];

// Gift Card Visual Preview Component
const GiftCardPreview = ({ design, amount, recipientName, senderName, practiceName, message }) => {
  const IconComponent = design?.icon || Gift;
  
  return (
    <div 
      className="relative rounded-2xl overflow-hidden shadow-xl aspect-[1.6/1] w-full max-w-md mx-auto"
      style={{ backgroundColor: design?.backgroundColor || '#DCC3B0' }}
    >
      {/* Pattern overlay */}
      <div className="absolute inset-0 opacity-10">
        {design?.pattern === 'confetti' && (
          <div className="absolute inset-0" style={{ 
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.4'%3E%3Ccircle cx='5' cy='5' r='3'/%3E%3Ccircle cx='35' cy='15' r='2'/%3E%3Ccircle cx='15' cy='35' r='2'/%3E%3Ccircle cx='45' cy='45' r='3'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }} />
        )}
        {design?.pattern === 'hearts' && (
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 30l-8-8a5.66 5.66 0 010-8 5.66 5.66 0 018 0 5.66 5.66 0 018 0 5.66 5.66 0 010 8l-8 8z' fill='%23000' fill-opacity='0.3'/%3E%3C/svg%3E")`,
            backgroundSize: '40px 40px'
          }} />
        )}
        {design?.pattern === 'snowflakes' && (
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='50' height='50' viewBox='0 0 50 50' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M25 0v50M0 25h50M7 7l36 36M7 43l36-36' stroke='%23000' stroke-opacity='0.3' stroke-width='1' fill='none'/%3E%3C/svg%3E")`,
            backgroundSize: '50px 50px'
          }} />
        )}
        {design?.pattern === 'stars' && (
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5l3 9h9l-7 5 3 9-8-6-8 6 3-9-7-5h9z' fill='%23000' fill-opacity='0.2'/%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }} />
        )}
      </div>
      
      {/* Content */}
      <div className="relative h-full p-6 flex flex-col justify-between">
        {/* Top section */}
        <div className="flex justify-between items-start">
          <div>
            <p className="text-xs uppercase tracking-wider opacity-70" style={{ color: design?.textColor }}>
              Gift Card
            </p>
            <p className="font-bold text-lg" style={{ color: design?.textColor }}>
              {practiceName || 'MdSpa360'}
            </p>
          </div>
          <div 
            className="p-3 rounded-full"
            style={{ backgroundColor: design?.accentColor, opacity: 0.9 }}
          >
            <IconComponent className="h-6 w-6 text-white" />
          </div>
        </div>
        
        {/* Center - Amount */}
        <div className="text-center">
          <p className="text-5xl font-bold" style={{ color: design?.textColor }}>
            ${amount || '100'}
          </p>
          <p className="text-sm mt-1 opacity-70" style={{ color: design?.textColor }}>
            {design?.tagline || 'A Gift of Relaxation'}
          </p>
        </div>
        
        {/* Bottom section */}
        <div className="flex justify-between items-end">
          <div>
            {recipientName && (
              <>
                <p className="text-xs opacity-60" style={{ color: design?.textColor }}>To:</p>
                <p className="font-semibold" style={{ color: design?.textColor }}>{recipientName}</p>
              </>
            )}
          </div>
          <div className="text-right">
            {senderName && (
              <>
                <p className="text-xs opacity-60" style={{ color: design?.textColor }}>From:</p>
                <p className="font-semibold" style={{ color: design?.textColor }}>{senderName}</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const GiftCards = () => {
  const { toast } = useToast();
  const { branding } = useBranding();
  
  // Multi-step form state
  const [step, setStep] = useState(1); // 1: Design, 2: Amount, 3: Details, 4: Review/Pay
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [amount, setAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [sendNow, setSendNow] = useState(true);
  
  const [processing, setProcessing] = useState(false);
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const [giftCardCode, setGiftCardCode] = useState('');

  const practiceName = branding?.practiceName || 'MdSpa360';

  const handleSelectDesign = (design) => {
    setSelectedDesign(design);
  };

  const handleSelectAmount = (amt) => {
    setAmount(amt);
    setUseCustomAmount(false);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (value) => {
    setCustomAmount(value);
    setUseCustomAmount(true);
    const parsed = parseFloat(value);
    if (!isNaN(parsed) && parsed >= 25 && parsed <= 500) {
      setAmount(parsed);
    }
  };

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 1:
        return selectedDesign !== null;
      case 2:
        return amount >= 25 && amount <= 500;
      case 3:
        return recipientName.trim() && recipientEmail.trim() && senderName.trim();
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    } else {
      toast({ 
        title: 'Please complete this step', 
        description: step === 1 ? 'Select a design' : step === 2 ? 'Choose an amount' : 'Fill in required fields',
        variant: 'destructive' 
      });
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handlePurchase = async () => {
    setProcessing(true);
    try {
      const purchaseData = {
        practiceId: branding?.practiceId || 'demo-practice-001',
        templateId: selectedDesign.id,
        designName: selectedDesign.name,
        amount: amount,
        recipientName,
        recipientEmail,
        senderName,
        senderEmail,
        personalMessage,
        deliveryDate: sendNow ? null : deliveryDate,
        designConfig: {
          backgroundColor: selectedDesign.backgroundColor,
          accentColor: selectedDesign.accentColor,
          textColor: selectedDesign.textColor,
          pattern: selectedDesign.pattern,
          tagline: selectedDesign.tagline
        }
      };

      const response = await fetch(`${API}/gift-cards/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(purchaseData)
      });

      if (!response.ok) throw new Error('Purchase failed');
      
      const result = await response.json();
      setGiftCardCode(result.code);
      setPurchaseComplete(true);
      
      toast({ title: 'Gift Card Purchased!', description: 'The recipient will receive their gift card via email.' });
    } catch (err) {
      toast({ title: 'Purchase Failed', description: 'Please try again or contact support.', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  if (purchaseComplete) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white py-12">
          <div className="container mx-auto px-4 max-w-2xl">
            <Card className="text-center">
              <CardContent className="py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="h-10 w-10 text-green-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">Gift Card Purchased!</h1>
                <p className="text-gray-600 mb-8">
                  Your gift card has been {sendNow ? 'sent' : 'scheduled'} to {recipientName} at {recipientEmail}.
                </p>
                
                <GiftCardPreview 
                  design={selectedDesign}
                  amount={amount}
                  recipientName={recipientName}
                  senderName={senderName}
                  practiceName={practiceName}
                />
                
                <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Gift Card Code</p>
                  <p className="text-2xl font-mono font-bold text-primary">{giftCardCode}</p>
                </div>
                
                <div className="mt-8 flex gap-4 justify-center">
                  <Button variant="outline" onClick={() => window.location.href = '/'}>
                    Return Home
                  </Button>
                  <Button onClick={() => {
                    setPurchaseComplete(false);
                    setStep(1);
                    setSelectedDesign(null);
                    setAmount(100);
                    setRecipientName('');
                    setRecipientEmail('');
                    setSenderName('');
                    setSenderEmail('');
                    setPersonalMessage('');
                  }}>
                    Buy Another Gift Card
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white py-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Give the Gift of Relaxation
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Send a {practiceName} gift card to someone special. Choose a beautiful design, 
              select an amount, and make their day.
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-center mb-12">
            <div className="flex items-center gap-2">
              {[
                { num: 1, label: 'Design' },
                { num: 2, label: 'Amount' },
                { num: 3, label: 'Details' },
                { num: 4, label: 'Review' }
              ].map((s, idx) => (
                <React.Fragment key={s.num}>
                  <div 
                    className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                      step === s.num 
                        ? 'bg-primary text-white' 
                        : step > s.num 
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    {step > s.num ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
                        {s.num}
                      </span>
                    )}
                    <span className="text-sm font-medium hidden sm:inline">{s.label}</span>
                  </div>
                  {idx < 3 && <ChevronRight className="h-4 w-4 text-gray-300" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Step 1: Choose Design */}
            {step === 1 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Choose a Design</CardTitle>
                  <CardDescription>Select a beautiful design that matches the occasion</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {GIFT_CARD_DESIGNS.map((design) => {
                      const IconComponent = design.icon;
                      const isSelected = selectedDesign?.id === design.id;
                      return (
                        <button
                          key={design.id}
                          onClick={() => handleSelectDesign(design)}
                          className={`relative p-4 rounded-xl border-2 transition-all ${
                            isSelected 
                              ? 'border-primary ring-2 ring-primary/20' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          style={{ backgroundColor: design.backgroundColor }}
                          data-testid={`design-${design.id}`}
                        >
                          {isSelected && (
                            <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                              <Check className="h-3 w-3 text-white" />
                            </div>
                          )}
                          <div 
                            className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2"
                            style={{ backgroundColor: design.accentColor }}
                          >
                            <IconComponent className="h-6 w-6 text-white" />
                          </div>
                          <p className="text-sm font-medium text-center" style={{ color: design.textColor }}>
                            {design.name}
                          </p>
                        </button>
                      );
                    })}
                  </div>

                  {/* Preview */}
                  {selectedDesign && (
                    <div className="mt-8">
                      <p className="text-sm text-gray-500 text-center mb-4">Preview</p>
                      <GiftCardPreview 
                        design={selectedDesign}
                        amount={amount}
                        recipientName="Jane Doe"
                        senderName="John Smith"
                        practiceName={practiceName}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step 2: Choose Amount */}
            {step === 2 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Choose an Amount</CardTitle>
                  <CardDescription>Select a preset amount or enter a custom value ($25 - $500)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-6">
                    {DENOMINATIONS.map((denom) => (
                      <button
                        key={denom}
                        onClick={() => handleSelectAmount(denom)}
                        className={`py-4 px-6 rounded-xl border-2 transition-all ${
                          amount === denom && !useCustomAmount
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`amount-${denom}`}
                      >
                        <span className="text-xl font-bold">${denom}</span>
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 mb-8">
                    <div className="flex-1">
                      <Label>Custom Amount</Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          type="number"
                          placeholder="Enter amount"
                          value={customAmount}
                          onChange={(e) => handleCustomAmountChange(e.target.value)}
                          min={25}
                          max={500}
                          className="pl-10"
                          data-testid="custom-amount-input"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Min: $25 | Max: $500</p>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="mt-8">
                    <p className="text-sm text-gray-500 text-center mb-4">Preview</p>
                    <GiftCardPreview 
                      design={selectedDesign}
                      amount={amount}
                      recipientName="Jane Doe"
                      senderName="John Smith"
                      practiceName={practiceName}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Recipient & Sender Details */}
            {step === 3 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Gift Card Details</CardTitle>
                  <CardDescription>Enter recipient information and a personal message</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Recipient Info */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Mail className="h-5 w-5 text-primary" />
                      Recipient Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="recipientName">Recipient Name *</Label>
                        <Input
                          id="recipientName"
                          placeholder="Jane Doe"
                          value={recipientName}
                          onChange={(e) => setRecipientName(e.target.value)}
                          className="mt-1"
                          data-testid="recipient-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="recipientEmail">Recipient Email *</Label>
                        <Input
                          id="recipientEmail"
                          type="email"
                          placeholder="jane@example.com"
                          value={recipientEmail}
                          onChange={(e) => setRecipientEmail(e.target.value)}
                          className="mt-1"
                          data-testid="recipient-email-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Sender Info */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <User className="h-5 w-5 text-primary" />
                      Your Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="senderName">Your Name *</Label>
                        <Input
                          id="senderName"
                          placeholder="John Smith"
                          value={senderName}
                          onChange={(e) => setSenderName(e.target.value)}
                          className="mt-1"
                          data-testid="sender-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="senderEmail">Your Email</Label>
                        <Input
                          id="senderEmail"
                          type="email"
                          placeholder="john@example.com"
                          value={senderEmail}
                          onChange={(e) => setSenderEmail(e.target.value)}
                          className="mt-1"
                          data-testid="sender-email-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Personal Message */}
                  <div>
                    <Label htmlFor="message">Personal Message (Optional)</Label>
                    <Textarea
                      id="message"
                      placeholder="Write a heartfelt message to the recipient..."
                      value={personalMessage}
                      onChange={(e) => setPersonalMessage(e.target.value)}
                      rows={4}
                      className="mt-1"
                      maxLength={500}
                      data-testid="personal-message-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">{personalMessage.length}/500 characters</p>
                  </div>

                  {/* Delivery Options */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary" />
                      Delivery Options
                    </h3>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={sendNow}
                          onChange={() => setSendNow(true)}
                          className="w-4 h-4 text-primary"
                        />
                        <span>Send Now</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={!sendNow}
                          onChange={() => setSendNow(false)}
                          className="w-4 h-4 text-primary"
                        />
                        <span>Schedule Delivery</span>
                      </label>
                    </div>
                    {!sendNow && (
                      <div className="mt-4">
                        <Label htmlFor="deliveryDate">Delivery Date</Label>
                        <Input
                          id="deliveryDate"
                          type="date"
                          value={deliveryDate}
                          onChange={(e) => setDeliveryDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="mt-1 max-w-xs"
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 4: Review & Pay */}
            {step === 4 && (
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-2xl">Review Your Gift Card</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <GiftCardPreview 
                      design={selectedDesign}
                      amount={amount}
                      recipientName={recipientName}
                      senderName={senderName}
                      practiceName={practiceName}
                    />
                    
                    {personalMessage && (
                      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500 mb-1">Your Message:</p>
                        <p className="text-gray-700 italic">"{personalMessage}"</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Order Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Design</span>
                        <span className="font-medium">{selectedDesign?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Gift Card Amount</span>
                        <span className="font-medium">${amount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">To</span>
                        <span className="font-medium">{recipientName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email</span>
                        <span className="font-medium text-sm">{recipientEmail}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Delivery</span>
                        <span className="font-medium">{sendNow ? 'Immediately' : deliveryDate}</span>
                      </div>
                    </div>

                    <hr />

                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span className="text-primary">${amount.toFixed(2)}</span>
                    </div>

                    <Button 
                      className="w-full mt-6" 
                      size="lg"
                      onClick={handlePurchase}
                      disabled={processing}
                      data-testid="purchase-btn"
                    >
                      {processing ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-5 w-5 mr-2" />
                          Complete Purchase
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-center text-gray-500 mt-4">
                      By completing this purchase, you agree to our Terms of Service. 
                      Gift cards are valid for 12 months from purchase date.
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Navigation Buttons */}
            {step < 4 && (
              <div className="flex justify-between mt-8">
                <Button 
                  variant="outline" 
                  onClick={prevStep}
                  disabled={step === 1}
                  data-testid="prev-step-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={nextStep}
                  disabled={!validateStep(step)}
                  data-testid="next-step-btn"
                >
                  Continue
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}

            {step === 4 && (
              <div className="flex justify-start mt-8">
                <Button variant="outline" onClick={prevStep} data-testid="prev-step-btn">
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to Details
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default GiftCards;
